import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { inputCurrency, inputCurrencyWithDot } from '@/libs/antd/input-number';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	getNAPRecommendationDetail,
	NAPDetail,
	NAPRecommendationPayload,
	NAPState,
	submitNAPRecommendation,
} from '@/libs/fleet-api/services/nap';
import {
	DeleteOutlined,
	EditOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment } from 'react';
import CovenantCreditModal, {
	useCovenantCreditModalModal,
} from './modals/CovenantCreditModal';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useModals } from '@/components/modals/ModalProvider';

type NAPRecommendationForm = NAPRecommendationPayload;

const NAPRecommendation: FC<{
	plafonType: NAPDetail['plafonType'];
	prospectId: string;
	customerId: string;
	napState?: NAPState;
	onSubmitted: () => void;
}> = ({ plafonType, customerId, prospectId, napState, onSubmitted }) => {
	const { modal } = useModals();
	const { groupName } = useUserData() ?? {};
	const { form, value, fieldName } = useForm<NAPRecommendationForm>();
	const getNAPRecommendationQuery = useQuery({
		queryKey: ['getNAPRecommendationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRecommendationDetail(id);
		},
		enabled: !!prospectId,
	});

	const submitNAPRecommendationMutation = useMutation({
		mutationFn: submitNAPRecommendation,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const covenantCreditModalModal = useCovenantCreditModalModal();

	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getNAPRecommendationQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const { data } = getNAPRecommendationQuery.data ?? {};

	return (
		<Fragment>
			<CovenantCreditModal
				{...covenantCreditModalModal}
				categoryOptions={data?.categoryOptions ?? []}
				priorityOptions={data?.priorityOptions ?? []}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 6 }}
				wrapperCol={{ span: 18 }}
				onFinish={async (values) => {
					const payload = {
						...values,
						downPayment: values.downPayment.toString(),
						effectiveInterestRate: values.effectiveInterestRate.toString(),
						administrationFee: values.administrationFee.toString(),
						provisionFee: values.provisionFee.toString(),
						servicingFee: values.servicingFee.toString(),
						fiduciaryFee: values.fiduciaryFee.toString(),
					};
					const res =
						await submitNAPRecommendationMutation.mutateAsync(payload);

					if (res.data.napNo) {
						await modal.success({
							title: (
								<strong style={{ fontSize: 20 }}>
									Berikut adalah NAP ID Anda
								</strong>
							),
							subTitle: (
								<strong style={{ fontSize: 24, color: '#1557BC' }}>
									{res.data.napNo}
								</strong>
							),
							cancelButtonProps: {
								hidden: false,
							},
							cancelText: 'BACK',
							okText: 'NEXT',
						});
					}

					onSubmitted();
				}}
			>
				<strong>
					<i>
						Berdasarkan evaluasi di atas maka kami mengusulkan untuk dapat
						disetujui fasilitas pembiayaan atas nama debitur dengan ketentuan
						dan syarat sebagai berikut:
					</i>
				</strong>
				<div style={{ marginBlock: 20 }}>
					<FleetTitle>Ketentuan</FleetTitle>
				</div>

				<div
					style={{
						background: '#1557BC',
						color: 'white',
						paddingInline: 20,
						paddingBlock: 10,
						marginBottom: 20,
					}}
				>
					<Row>
						<Col span={6}>
							<strong>Perihal</strong>
						</Col>
						<Col span={18}>
							<strong>Usulan</strong>
						</Col>
					</Row>
				</div>

				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>

				<Form.Item
					label="Limit BPP"
					name={fieldName('bppLimit')}
					rules={[rule.required()]}
					initialValue={data?.bppLimit}
				>
					<InputNumber
						{...inputCurrency}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Jangka Waktu Plafon"
					name={fieldName('plafonPeriod')}
					rules={[rule.required()]}
					initialValue={data?.plafonPeriod}
				>
					<Input disabled={isReadOnly} />
				</Form.Item>

				<Divider />

				<Form.Item label="Jenis Pembiayaan">
					<Checkbox.Group
						defaultValue={
							data?.financingTypes?.map((option) => option.id) ?? []
						}
					>
						<Row gutter={[20, 10]}>
							{data?.financingTypeOptions?.map((option) => (
								<Col
									span={
										data?.financingTypes?.some(
											(financing) => financing.id === option.id,
										)
											? 24
											: 12
									}
									key={option.id}
								>
									<Checkbox
										value={option.id}
										disabled
									>
										<span style={{ color: 'black' }}>
											{data?.financingTypes?.some(
												(financing) => financing.id === option.id,
											) ? (
												<strong>{option.value}</strong>
											) : (
												option.value
											)}
										</span>
									</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Sifat Limit Pembiayaan"
					name={fieldName('financingLimitTypeId')}
					rules={[rule.required()]}
					initialValue={data?.financingLimitType?.id}
				>
					<Radio.Group>
						{data?.financingLimitTypeOptions?.map((data) => (
							<Radio
								key={data.id}
								value={data.id}
								disabled={isReadOnly}
							>
								{data.value}
							</Radio>
						))}
					</Radio.Group>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Objek Pembiayaan"
					name={fieldName('financingObjectIds')}
					initialValue={data?.financingObjects?.map((data) => data.id) ?? []}
				>
					<Checkbox.Group>
						<Row gutter={[20, 10]}>
							{data?.financingObjectOptions?.map((data) => (
								<Col
									span={6}
									key={data.id}
								>
									<Checkbox
										value={data.id}
										disabled={isReadOnly}
									>
										<span style={{ color: 'black' }}>{data.value}</span>
									</Checkbox>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Down Payment"
					name={fieldName('downPayment')}
					rules={[rule.required()]}
					initialValue={data?.downPayment}
				>
					<InputNumber
						{...inputCurrencyWithDot}
						disabled={isReadOnly}
						controls={false}
						suffix="%"
						max={100}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Suku Bunga Efektif"
					name={fieldName('effectiveInterestRate')}
					rules={[rule.required()]}
					initialValue={data?.effectiveInterestRate}
				>
					<InputNumber
						{...inputCurrencyWithDot}
						disabled={isReadOnly}
						controls={false}
						suffix="%"
						max={100}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Tenor"
					name={fieldName('tenor')}
					rules={[rule.required()]}
					initialValue={data?.tenor}
				>
					<InputNumber
						controls={false}
						disabled={isReadOnly}
						suffix={<span style={{ color: '#bababa' }}>Bulan</span>}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Biaya Administrasi"
					name={fieldName('administrationFee')}
					rules={[rule.required()]}
					initialValue={data?.administrationFee}
				>
					<InputNumber
						{...inputCurrency}
						disabled={isReadOnly}
						controls={false}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Provisi"
					name={fieldName('provisionFee')}
					rules={[rule.required()]}
					initialValue={data?.provisionFee}
				>
					<InputNumber
						{...inputCurrencyWithDot}
						disabled={isReadOnly}
						controls={false}
						suffix="%"
						max={100}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Servicing Fee"
					name={fieldName('servicingFee')}
					initialValue={data?.servicingFee}
					rules={[rule.required()]}
				>
					<InputNumber
						{...inputCurrency}
						disabled={isReadOnly}
						controls={false}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Fidusia"
					name={fieldName('fiduciaryFee')}
					initialValue={data?.fiduciaryFee}
					rules={[rule.required()]}
				>
					<InputNumber
						{...inputCurrency}
						disabled={isReadOnly}
						controls={false}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Asuransi"
					name={fieldName('insuranceId')}
					initialValue={data?.insurance?.id}
					rules={data?.insuranceOptions?.length ? [rule.required()] : []}
				>
					{data?.insuranceOptions?.length ? (
						<Radio.Group>
							{data?.insuranceOptions?.map((data) => (
								<Radio
									key={data.id}
									value={data.id}
									disabled={isReadOnly}
								>
									{data.value}
								</Radio>
							))}
						</Radio.Group>
					) : (
						<span style={{ color: '#bababa' }}>no data</span>
					)}
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('insurancePayment')}
					initialValue={data?.insurancePayment ?? ''}
					noStyle
				/>
				{/* <Form.Item
					label="Payment Asuransi"
					name={fieldName('insurancePayment')}
					initialValue={data?.insurancePayment}
					rules={[rule.required()]}
				>
					<Input disabled={isReadOnly} />
				</Form.Item>

				<Divider /> */}

				<Form.Item
					label="Way Of Payment (WOP)"
					name={fieldName('paymentMethodId')}
					initialValue={data?.paymentMethod?.id}
					rules={[rule.required()]}
				>
					<Select
						loading={getNAPRecommendationQuery.isFetching}
						options={
							data?.paymentMethodOptions?.map((data) => {
								return {
									label: data.value,
									value: data.id,
								};
							}) ?? []
						}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />
				<Form.Item
					name={fieldName('evidenceFile')}
					label="Upload Evidence"
					initialValue={data?.evidenceFile}
					rules={
						value?.specialCondition !== null && value?.specialCondition !== ''
							? [rule.required()]
							: []
					}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={
							isReadOnly ||
							groupName !== 'CCA' ||
							value?.specialCondition === null ||
							value?.specialCondition === ''
						}
						beforeUpload={() => false}
						style={{
							width: 260,
							backgroundColor:
								isReadOnly || groupName !== 'CCA' ? '#f3f3f3' : undefined,
						}}
						onRemove={() => {
							form.setFieldValue(fieldName('evidenceFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(fieldName('evidenceFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('evidenceFile'), null);
							}
						}}
					>
						{uploadMutation.isPending ? (
							<LoadingOutlined
								style={{
									fontSize: 25,
									color: '#BABABA',
									display: 'block',
								}}
							/>
						) : (
							<UploadOutlined
								style={{
									fontSize: 25,
									color: '#BABABA',
									display: 'block',
								}}
							/>
						)}
					</Upload.Dragger>
				</Form.Item>

				<Divider />

				<div style={{ marginBlock: 20 }}>
					<FleetTitle>List Ketentuan & Syarat Kredit</FleetTitle>
				</div>

				{groupName === 'CCA' && (
					<Row style={{ marginBlock: 20 }}>
						<Col flex="auto" />
						<Col flex="none">
							<Button
								style={{
									borderRadius: 50,
									fontSize: 12,
									height: 24,
									fontWeight: 700,
									...(!isReadOnly && {
										borderColor: '#1557BC',
										color: '#1557BC',
									}),
								}}
								icon={'+'}
								disabled={isReadOnly}
								onClick={async () => {
									const newData = await covenantCreditModalModal.open();
									if (!newData) return;

									form.setFieldValue(fieldName('creditTermsAndConditions'), [
										...(value?.creditTermsAndConditions ?? []),
										newData,
									]);
								}}
							>
								Add Another
							</Button>
						</Col>
					</Row>
				)}

				<Form.Item
					name={fieldName('creditTermsAndConditions')}
					initialValue={
						data?.creditTermsAndConditions?.map((data) => {
							return {
								id: data.id,
								categoryId: data.category.id,
								priorityId: data.priority.id,
								termAndCondition: data.termAndCondition,
							};
						}) ?? []
					}
					noStyle
				/>

				<Table
					bordered
					pagination={false}
					dataSource={value?.creditTermsAndConditions?.map((data, i) => {
						return {
							...data,
							key: i + 1,
						};
					})}
					columns={[
						{
							hidden: groupName !== 'CCA',
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={async (e) => {
										e.preventDefault();
										if (!isReadOnly) {
											const editData =
												await covenantCreditModalModal.open(record);
											if (!editData) return;

											form.setFieldValue(
												fieldName('creditTermsAndConditions'),
												value?.creditTermsAndConditions?.map(
													(data, dataIndex) => {
														return index === dataIndex ? editData : data;
													},
												),
											);
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							hidden: groupName !== 'CCA',
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											form.setFieldValue(
												fieldName('creditTermsAndConditions'),
												value?.creditTermsAndConditions?.filter(
													(_, i) => index !== i,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Ketentuan & Syarat',
							render: (_, record) => record?.termAndCondition,
						},
						{
							title: 'Kategori',
							render: (_, record) =>
								data?.categoryOptions?.find(
									(data) => data.id === record?.categoryId,
								)?.value ?? record.categoryId,
						},
						{
							title: 'Priority',
							render: (_, record) =>
								data?.priorityOptions.find(
									(data) => data.id === record?.priorityId,
								)?.value ?? record.priorityId,
						},
					]}
				/>

				<Form.Item
					label={
						plafonType === 'JOIN_FINANCING'
							? groupName === 'CCA'
								? 'Informasi Tambahan'
								: 'Notes CCA'
							: 'Verification Notes'
					}
					style={{ marginTop: 20 }}
					name={fieldName('verificationNotes')}
					initialValue={data?.verificationNotes}
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={submitNAPRecommendationMutation.isPending || isReadOnly}
					loading={submitNAPRecommendationMutation.isPending}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPRecommendation;
